import React from 'react';

import {
  Breakpoints,
  ButtonAppearance,
  ButtonCustomLink,
  ChevronRight,
  CustomerCenter,
  DisableFocusContainer,
  Link,
  LinkButton,
  ModalContainer,
  PinPlace,
  TextAppearance,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
  useIsMobile,
} from '@vw-marketing/us-components';

import {
  StyledDealerWebSiteContainer,
  StyledFixedCloseButton,
  StyledInterstitialCards,
  StyledInterstitialContainer,
  StyledInterstitialDealerWebSite,
  StyledInterstitialStepCards,
  StyledInterstitialTitle,
} from './styles';
import {UsedVehicle} from '../../typing/used-inventory';
import {useTranslations} from '../../hooks/use-translations';
import {
  cancelButtonStyle,
  cardSubtitleTextStyle,
  dealerCTATextStyle,
  stepIconMap,
  titleTextStyle,
} from './helpers';
import {InterstitialCarCard} from './interstitial-car-card/InterstitialCarCard';
import {InterstitialCard} from './interstitial-card/InterstitialCard';
import {ModalHandOffStepTranslation} from '../../typing/translations';
import {
  getOnlineBuyingDeeplinkData,
  getOnlineBuyingProvider,
  stringToPhoneFormat,
} from '../../utils/general';
import {useFeatureAppConfig} from '../../hooks/use-feature-app-config';
import {
  onVWBasicVehicleButtonClickEvent,
  onVWBasicVehicleTelephoneNumberClick,
} from '../../tagging/tagging-helpers';
import {useTrackingManager} from '../../hooks/use-tracking-manager';
import {
  StyledDealerCardInformation,
  StyledDealerInformationWrapper,
} from './interstitial-car-card/styles';
import {DealerInformationDetail} from '../dealer-information-detail/DealerInformationDetail';
import {dealerNameTextStyle} from './interstitial-car-card/helpers';

export interface ModalBuildDealProps {
  active: boolean;
  vehicle: UsedVehicle;
  handOffContent: string;
  onCloseHandler: () => void;
  vehiclePosition?: number;
}

export const ModalBuildDeal = ({
  active,
  onCloseHandler,
  handOffContent,
  vehicle,
}: ModalBuildDealProps) => {
  const isMobile = useIsMobile(Breakpoints.b560);
  const trackingManager = useTrackingManager();
  const {obLink, isLinKModified} = getOnlineBuyingDeeplinkData(vehicle);
  const modalsContent = useTranslations().modals;
  const handOffContentMap = {
    NoOnlineBuying: modalsContent.modalHandOffNoBuying,
    onlineBuying: modalsContent.modalHandOffOnlineBuying,
  };
  const translations = handOffContentMap[handOffContent];

  const modalCtaLabel = isLinKModified
    ? translations.dealerWebSiteModified
    : translations.dealerWebSite;

  const onDealerButtonClick = (vehicleModel: UsedVehicle) => {
    const informationVendor = getOnlineBuyingProvider(vehicleModel);

    const trackingLink = obLink
      ? {
          link: {
            url: obLink,
            name: 'online buying',
            newTab: true,
            isDealerPage: true,
          },
        }
      : {};

    const trackingPartner = informationVendor
      ? {partner: {informationVendor}}
      : {};

    const additionalTrackingData = {
      ...trackingLink,
      ...trackingPartner,
    };

    onVWBasicVehicleButtonClickEvent(
      vehicleModel,
      {
        Type: 'Button',
        Name: modalCtaLabel,
        ItemCount: 1,
        ItemPosition: 1,
      },
      trackingManager,
      additionalTrackingData,
    );

    onCloseHandler();
  };

  const onHandleTelephoneClick = () => {
    const linkParams = {
      url: window.location.href,
      name: undefined,
    };

    trackingManager &&
      onVWBasicVehicleTelephoneNumberClick(
        trackingManager,
        vehicle,
        linkParams,
      );
  };

  return (
    <ModalContainer
      active={active}
      onCloseHandler={onCloseHandler}
      ariaLabel="Build my deal Modal"
      closeButtonAriaLabel="Close Build my deal Modal"
    >
      <StyledInterstitialContainer className="modal-build-deal-interstitial">
        <StyledInterstitialTitle className="modal-build-deal-interstitial-title">
          <TextCustom
            appearance={TextAppearance.headline300}
            tag={TextTag.h2}
            style={titleTextStyle}
          >
            {translations.title}
          </TextCustom>
        </StyledInterstitialTitle>
        <StyledInterstitialStepCards className="modal-build-deal-interstitial-cards">
          <div className="vehicle-card-wrapper">
            <InterstitialCarCard
              vehicle={vehicle}
              translations={translations}
            />
          </div>
          <StyledInterstitialCards className="modal-build-deal-interstitial-cards">
            <StyledDealerCardInformation className="dealer-card-dealer-information">
              <TextCustom {...dealerNameTextStyle}>
                <span className="notranslate">{vehicle.dealer?.name}</span>
              </TextCustom>
              <TextCustom
                appearance={TextAppearance.headline200}
                tag={TextTag.p}
                style={{
                  fontWeight: TextWeight.light,
                  fontSize: TextSize.TextSize16,
                  lineHeight: TextLineHeight.TextLineHeight20,
                }}
              >
                {`${vehicle?.dealer?.distance.toFixed(2)} ${
                  translations.dealerStepCard.distance
                }`}
              </TextCustom>
              <StyledDealerInformationWrapper>
                <DealerInformationDetail
                  Icon={PinPlace}
                  label1={
                    <span className="notranslate">
                      {vehicle.dealer?.address1}
                    </span>
                  }
                  label2={
                    <span className="notranslate">
                      {vehicle.dealer?.city +
                        ', ' +
                        vehicle.dealer?.state +
                        ' ' +
                        vehicle.dealer?.postalcode}
                    </span>
                  }
                />
                <DealerInformationDetail
                  Icon={CustomerCenter}
                  label1={
                    isMobile ? (
                      // @ts-ignore
                      <Link
                        href={
                          vehicle.dealer ? `tel: ${vehicle.dealer.phone}` : ''
                        }
                        isInline
                        onClick={onHandleTelephoneClick}
                      >
                        {
                          <span className="notranslate">
                            {stringToPhoneFormat(vehicle?.dealer?.phone)}
                          </span>
                        }
                      </Link>
                    ) : (
                      <span className="notranslate">
                        {stringToPhoneFormat(vehicle?.dealer?.phone)}
                      </span>
                    )
                  }
                />
              </StyledDealerInformationWrapper>
            </StyledDealerCardInformation>
            <div className="interstitial-card">
              <div className="subtitle">
                <TextCustom {...cardSubtitleTextStyle}>
                  {translations.subtitle}
                </TextCustom>
              </div>
              {translations.steps.map((step: ModalHandOffStepTranslation) => (
                <InterstitialCard
                  classes={`${step.id} step`}
                  Icon={stepIconMap[step.icon]}
                  description={step.descriptionMarkdown}
                />
              ))}
            </div>
          </StyledInterstitialCards>
          {!isMobile && (
            <StyledDealerWebSiteContainer className="modal-build-deal-interstitial-button">
              <DisableFocusContainer>
                <StyledInterstitialDealerWebSite
                  className="hand-off-dealer-link"
                  isLarge={handOffContent === 'onlineBuying'}
                >
                  {/* @ts-ignore */}
                  <ButtonCustomLink
                    appearance={ButtonAppearance.Primary}
                    href={obLink}
                    target={'_blank'}
                    onClick={onDealerButtonClick.bind(null, vehicle)}
                  >
                    <TextCustom {...dealerCTATextStyle}>
                      {modalCtaLabel}
                    </TextCustom>
                    <ChevronRight variant="small" />
                  </ButtonCustomLink>
                  {/* @ts-ignore */}
                  <LinkButton onClick={onCloseHandler} isInline>
                    <TextCustom {...cancelButtonStyle}>Cancel</TextCustom>
                  </LinkButton>
                </StyledInterstitialDealerWebSite>
              </DisableFocusContainer>
            </StyledDealerWebSiteContainer>
          )}
        </StyledInterstitialStepCards>
        {isMobile && (
          <StyledFixedCloseButton>
            {/* @ts-ignore */}
            <ButtonCustomLink
              appearance={ButtonAppearance.Primary}
              href={obLink}
              target={'_blank'}
              onClick={onDealerButtonClick.bind(null, vehicle)}
              stretchContent={true}
            >
              <TextCustom {...dealerCTATextStyle}>{modalCtaLabel}</TextCustom>
              <ChevronRight variant="small" />
            </ButtonCustomLink>
          </StyledFixedCloseButton>
        )}
      </StyledInterstitialContainer>
    </ModalContainer>
  );
};
